import { AdMob, BannerAdSize, BannerAdPosition } from '@capacitor-community/admob';
import { isAndroidCapacitor } from './platformUtils';

export const ADMOB_BANNER_AD_UNIT_ID = 'ca-app-pub-1511890644353474/8570664045';

let initialized = false;
let initPromise = null;
/** Route-level placement: 'none' | 'bottom' | 'infeed' */
let placement = 'none';
let bannerHeight = 100;
let activeInFeedKey = null;
let sizeSub = null;

const inFeedSlots = new Map(); // key -> HTMLElement

function setInset(px) {
    document.documentElement.style.setProperty('--admob-bottom-inset', `${Math.max(0, px)}px`);
}

export function getAdMobBannerHeight() {
    return bannerHeight;
}

export async function ensureAdMobReady() {
    if (!isAndroidCapacitor()) return false;
    if (initialized) return true;
    if (initPromise) return initPromise;

    initPromise = (async () => {
        await AdMob.initialize({
            requestTrackingAuthorization: false,
            initializeForTesting: false,
        });

        sizeSub = await AdMob.addListener('onAdSize', (info) => {
            const h = info?.height || 0;
            if (h > 0) bannerHeight = h;
            if (placement === 'bottom') {
                setInset(h);
            }
        });

        initialized = true;
        return true;
    })().catch((err) => {
        initPromise = null;
        console.error('[AdMob] init failed:', err);
        return false;
    });

    return initPromise;
}

async function removeBannerQuiet() {
    try {
        await AdMob.removeBanner();
    } catch (_) {
        /* ignore */
    }
}

let scrollListening = false;

function onScrollOrResize() {
    syncInFeedBanner();
}

function startInFeedScrollSync() {
    if (scrollListening || typeof window === 'undefined') return;
    scrollListening = true;
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
}

function stopInFeedScrollSync() {
    if (!scrollListening || typeof window === 'undefined') return;
    scrollListening = false;
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
}

export async function hideAdMobBanner() {
    if (!isAndroidCapacitor()) return;
    placement = 'none';
    activeInFeedKey = null;
    stopInFeedScrollSync();
    setInset(0);
    await removeBannerQuiet();
}

export async function showStickyBottomBanner() {
    if (!(await ensureAdMobReady())) return;

    placement = 'bottom';
    activeInFeedKey = null;
    inFeedSlots.clear();
    stopInFeedScrollSync();

    try {
        await AdMob.showBanner({
            adId: ADMOB_BANNER_AD_UNIT_ID,
            adSize: BannerAdSize.ADAPTIVE_BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
            margin: 0,
            isTesting: false,
        });
    } catch (err) {
        console.error('[AdMob] sticky bottom failed:', err);
    }
}

function marginForElement(el) {
    const rect = el.getBoundingClientRect();
    const h = bannerHeight || 100;
    return Math.max(0, Math.round(window.innerHeight - rect.top - h));
}

async function showInFeedBannerForSlot(key, el) {
    if (!(await ensureAdMobReady()) || !el || placement !== 'infeed') return;

    if (activeInFeedKey === key) return;
    activeInFeedKey = key;

    try {
        await AdMob.showBanner({
            adId: ADMOB_BANNER_AD_UNIT_ID,
            adSize: BannerAdSize.ADAPTIVE_BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
            margin: marginForElement(el),
            isTesting: false,
        });
    } catch (err) {
        console.error('[AdMob] in-feed banner failed:', err);
        activeInFeedKey = null;
    }
}

function pickPrimaryInFeedSlot() {
    let bestKey = null;
    let bestScore = 0;

    inFeedSlots.forEach((el, key) => {
        if (!el?.isConnected) return;
        const rect = el.getBoundingClientRect();
        const viewH = window.innerHeight || 1;
        const visible = Math.min(rect.bottom, viewH) - Math.max(rect.top, 0);
        if (visible <= 0) return;
        const ratio = visible / Math.max(rect.height, 1);
        const center = (rect.top + rect.bottom) / 2;
        const centerScore = 1 - Math.min(1, Math.abs(center - viewH / 2) / (viewH / 2));
        const score = ratio * 0.7 + centerScore * 0.3;
        if (score > bestScore) {
            bestScore = score;
            bestKey = key;
        }
    });

    return bestKey && bestScore >= 0.35 ? bestKey : null;
}

let syncScheduled = false;

export function syncInFeedBanner() {
    if (placement !== 'infeed') return;
    if (syncScheduled) return;
    syncScheduled = true;

    requestAnimationFrame(() => {
        syncScheduled = false;
        if (!isAndroidCapacitor() || placement !== 'infeed') return;

        const key = pickPrimaryInFeedSlot();
        if (!key) {
            if (activeInFeedKey) {
                activeInFeedKey = null;
                removeBannerQuiet();
            }
            return;
        }

        const el = inFeedSlots.get(key);
        if (!el) return;
        showInFeedBannerForSlot(key, el);
    });
}

export function registerInFeedSlot(key, el) {
    if (!key || !el) return () => {};
    inFeedSlots.set(key, el);
    syncInFeedBanner();
    return () => {
        inFeedSlots.delete(key);
        if (activeInFeedKey === key) {
            activeInFeedKey = null;
            syncInFeedBanner();
        }
    };
}

export async function enterInFeedMode() {
    if (!(await ensureAdMobReady())) return;
    placement = 'infeed';
    activeInFeedKey = null;
    setInset(0);
    await removeBannerQuiet();
    startInFeedScrollSync();
    syncInFeedBanner();
}

export function getAdMobPlacementMode() {
    return placement;
}

export function disposeAdMobController() {
    stopInFeedScrollSync();
    sizeSub?.remove?.();
    sizeSub = null;
    inFeedSlots.clear();
    initialized = false;
    initPromise = null;
    placement = 'none';
    activeInFeedKey = null;
}
